import { useQuery } from "@tanstack/react-query";
import { getBrandMagnet } from "apis/brand";
import Loading from "components/atoms/Loading";
import { useRouter } from "components/hooks";
import { toast } from "react-toastify";
import { getUpdateShareRequestRoute } from "apis/shareRequest";
import { get } from "utils/request";
import { useMemo } from "react";
import moment from "moment";
import ShareRequestPage from "./ShareRequestPage";
import { ROUTES, TOAST_CONFIG } from "../../constants";

function ShareRequest({ isEditForm }: { isEditForm?: boolean }) {
  const router = useRouter();
  const { shareRequestId, magnetId, brandId } = router.query;

  const handleGetClaimCodeFail = (errorMessage: string, error: any) => {
    toast.error(errorMessage, TOAST_CONFIG);
    if (error.response.status === 404) {
      router.go(`/${ROUTES.NOT_FOUND}`);
      return;
    }
    throw new Error(errorMessage);
  };

  const getShareRequestData = () =>
    get(getUpdateShareRequestRoute(magnetId, shareRequestId), {
      processError: handleGetClaimCodeFail,
    });

  const {
    isLoading: isClaimDataLoading,
    error: claimDataGetError,
    data: claimCodeData,
    isFetching: isClaimCodeDataFetching,
  } = useQuery({
    queryKey: ["share-request-id", magnetId, shareRequestId],
    queryFn: getShareRequestData,
    enabled: Boolean(isEditForm && shareRequestId),
  });

  const handleGetMagnetFailure = (errorMessage: string, error: any) => {
    toast.error(errorMessage, TOAST_CONFIG);
    if (error.response.status === 404) {
      router.go(`/${ROUTES.NOT_FOUND}`);
    }
  };

  const getMagnetDetails = () =>
    get(getBrandMagnet(brandId, magnetId), {
      processError: handleGetMagnetFailure,
    });

  // Get Magnet Details
  const {
    isLoading: isMagnetDetailsLoading,
    error: magnetDetailsError,
    data: magnetDetails,
  } = useQuery<BrandMagnetDetails>({
    queryKey: [brandId, magnetId],
    queryFn: getMagnetDetails,
    // when we switch browser tabs , data is refetched,
    refetchOnWindowFocus: false,
    // every time  react element is mounted, data is refetched.
    refetchOnMount: false,
  });

  const shareRequestExistingValues = useMemo(() => {
    if (!claimCodeData) return undefined;
    return {
      ...claimCodeData,
      start_time: moment(new Date(claimCodeData.start_time)),
      end_time: moment(new Date(claimCodeData.end_time)),
    };
  }, [claimCodeData]);

  if (isMagnetDetailsLoading) return <Loading />;
  if (magnetDetailsError) return <p>Unable to fetch Magnet details</p>;

  if (!magnetDetails) return <p>Unable to fetch Magnet details</p>;

  if (isEditForm && isClaimDataLoading && isClaimCodeDataFetching) {
    return <Loading />;
  }

  if (isEditForm && claimDataGetError) {
    return <p>Failed to get Claim</p>;
  }

  return (
    <ShareRequestPage
      magnetDetails={magnetDetails}
      shareRequestExistingValues={shareRequestExistingValues}
      isClaimCodePublished={[2, 3, 4, 5].includes(claimCodeData?.status)}
    />
  );
}

export default ShareRequest;
